// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import { MUST_SEARCH_AT_LEAST_ONE_VALUE } from 'errors'
import { valueIsInArray } from 'helpers/reduxFormFunctions'
import {
  assembleQuery,
  fetchRadiosByQuery,
  makePaginationParams,
} from 'helpers/api'

const supportsRange = [
  'throughput',
  'txPowerMax',
  'bandwidth',
  'frequency',
]

const eqList = [
  'architectureId',
  'bandwidth',
  'modulationTypeId',
  'radioTypeId',
  'statusId',
  'txPowerMax',
]

const parseAsFloat = [
  'architectureId',
  'modulationTypeId',
  'radioTypeId',
  'statusId',
  'throughput',
  'txPowerMax',
  'bandwidth',
  'frequency',
]

// checks to see if the value should be cast as a number for API consumption.
const castValueType = (k, v) => valueIsInArray(k, parseAsFloat) ? parseFloat(v) : v

// determines if the operator should be like or equal for the search object.
const setOperator = f => valueIsInArray(f, eqList) ? 'EQ' : 'LIKE'

// create a range search object
const createRangeSearchObject = (v, f) => ({
  field: f,
  operator: 'BETWEEN',
  valueMin: +v.split(/:/)[0],
  valueMax: +v.split(/:/)[1],
})

// create a basic search object
const createSearchObject = (v, f) => ({
  field: f,
  operator: setOperator(f),
  value: castValueType(f, v),
})

// check value to see if it should be parsed as a range or single value.
const checkForRangeAndCreateSearchObject = (v, f) => (
  ~v.indexOf(':')
    ? createRangeSearchObject(v, f)
    : createSearchObject(v, f)
)

// check if the field supports a range value.
const createSearchObjectByFieldAndValue = (v, f) => (
  ~supportsRange.indexOf(f)
    ? checkForRangeAndCreateSearchObject(v, f)
    : createSearchObject(v, f)
)

const fetchData = formArgs => {
  const {
    formProps,
    formValues,
    queryBasis = [],
    includeFlags = [],
    submittedFormIsPristine,
  } = formArgs

  const d = { ...formValues }
  const search = [...queryBasis]

  // advanced search input fields should be ignored if the advanced search section isn't displayed.
  const isAdvSearch = !!d.advancedSearch.length
  delete d.advancedSearch

  // captured to ensure queries are not made without any search values.
  let hasValue = false

  // allow user to give a radio favorite for the radio code
  // but actually search for that favorite's radio code.
  // eg. 558AA-1 ==> 558AA
  if (!~d.radioCode.indexOf(/-/)) {
    d.radioCode = d.radioCode.split('-')[0]
  }

  Object.keys(d).forEach(k => {
    // ignore pagination values when building the search query
    if (k === 'pagination') {
      return
    }

    // must be at 0
    const isAdvInput = !k.indexOf('adv-')

    // adv input values should only be added when adv search is open.
    // if it's not an adv input, always add the value
    const shouldApplyValue = isAdvSearch && isAdvInput || !isAdvInput

    // do not consider 'hasValue' for advanced input
    // values if it is not an advanced search
    if (!hasValue && d[k].length && shouldApplyValue) {
      hasValue = true
    }

    // don't send advanced input values in the query
    // when it's not not an advanced search
    if (d[k].length && shouldApplyValue) {
      const f = isAdvInput ? k.slice(4) : k
      const v = d[k]
      search.push(createSearchObjectByFieldAndValue(v, f))
    }
  })

  if (!hasValue) {
    throw new Error(MUST_SEARCH_AT_LEAST_ONE_VALUE)
  }

  const page = (
    formValues.pagination && submittedFormIsPristine(formProps.form)
      ? +formValues.pagination.page
      : 0
  )

  // size of results set in API response - default to 500 count
  const size = 500

  const paginationParams = makePaginationParams({
    page,
    size,
  })

  const searchQuery = {
    field: 'search',
    value: [search],
  }

  const query = [
    searchQuery,
    ...includeFlags,
    ...paginationParams,
  ]

  return fetchRadiosByQuery(assembleQuery(query))
}

export default fetchData
